// @ts-nocheck
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { markNotificationAsViewed } from "@/services/birthdayNotificationService";
import { ClientIntakePanel } from "@/components/clients/ClientIntakePanel";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Users, TrendingUp, UserCheck, Building, User, MapPin, FileText, Shield, Scale, ExternalLink, Clock, AlertTriangle, Calendar, X, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ClientForm } from "@/components/clients/ClientForm";
import { ClientCard, getLegalPriority, getClientPhone, whatsappLink, getInitials, birthdayMessage, birthdayInfo } from "@/components/clients/ClientCard";
import { ClientFilters } from "@/components/clients/ClientFilters";

interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  mobile?: string;
  cpf_cnpj?: string;
  client_type: 'person' | 'company';
  rg_ie?: string;
  birth_date?: string;
  nationality?: string;
  marital_status?: string;
  profession?: string;
  alternative_phone?: string;
  address?: string;
  address_number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  how_found_us?: string;
  referral_source?: string;
  legal_areas?: string[];
  client_priority?: 'low' | 'medium' | 'high' | 'vip';
  notes?: string;
  special_requirements?: string;
  confidentiality_level?: 'normal' | 'high' | 'confidential';
  lgpd_consent: boolean;
  lgpd_consent_date?: string;
  marketing_consent?: boolean;
  data_sharing_consent?: boolean;
  created_at: string;
}

const Clients = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientTypeFilter, setClientTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [legalAreaFilter, setLegalAreaFilter] = useState("all");
  const [birthdayClientId, setBirthdayClientId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientProcesses, setClientProcesses] = useState<any[]>([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Estados para modais de estatísticas
  // Indicador clicado filtra a própria lista (sem abrir janela)
  const [kpiFilter, setKpiFilter] = useState<{ type: string; title: string; ids: Set<string> } | null>(null);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsModalType, setStatsModalType] = useState<string>('');
  const [statsModalTitle, setStatsModalTitle] = useState<string>('');
  const [statsModalClients, setStatsModalClients] = useState<Client[]>([]);

  type FormDataType = {
    name: string;
    client_type: 'person' | 'company';
    cpf_cnpj: string;
    rg_ie: string;
    birth_date?: Date;
    nationality: string;
    marital_status: string;
    profession: string;
    email: string;
    phone: string;
    mobile: string;
    alternative_phone: string;
    address: string;
    address_number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    how_found_us: string;
    referral_source: string;
    legal_areas: string[];
    client_priority: 'low' | 'medium' | 'high' | 'vip';
    notes: string;
    special_requirements: string;
    confidentiality_level: 'normal' | 'high' | 'confidential';
    lgpd_consent: boolean;
    lgpd_consent_date?: Date;
    marketing_consent: boolean;
    data_sharing_consent: boolean;
  };

  const [formData, setFormData] = useState<FormDataType>({
    name: "",
    client_type: "person",
    cpf_cnpj: "",
    rg_ie: "",
    birth_date: undefined,
    nationality: "Brasileira",
    marital_status: "",
    profession: "",
    email: "",
    phone: "",
    mobile: "",
    alternative_phone: "",
    address: "",
    address_number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    zip_code: "",
    country: "Brasil",
    how_found_us: "",
    referral_source: "",
    legal_areas: [],
    client_priority: "medium",
    notes: "",
    special_requirements: "",
    confidentiality_level: "normal",
    lgpd_consent: false,
    lgpd_consent_date: undefined,
    marketing_consent: false,
    data_sharing_consent: false,
  });

  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [processCounts, setProcessCounts] = useState<Record<string, number>>({});

  // Quantidade de processos (judiciais ativos + administrativos) por cliente
  const fetchProcessCounts = async () => {
    try {
      const [casesRes, admRes] = await Promise.all([
        supabase.from('cases').select('*, case_clients(client_id)'),
        (supabase as any).from('administrative_processes').select('id, client_id'),
      ]);
      const counts: Record<string, number> = {};
      (casesRes.data || []).forEach((c: any) => {
        if (c.archived) return;
        const ids = new Set([c.client_id, ...(c.case_clients || []).map((cc: any) => cc.client_id)].filter(Boolean));
        ids.forEach((id: string) => { counts[id] = (counts[id] || 0) + 1; });
      });
      (admRes.data || []).forEach((p: any) => {
        if (p.client_id) counts[p.client_id] = (counts[p.client_id] || 0) + 1;
      });
      setProcessCounts(counts);
    } catch (error) {
      console.warn('Não foi possível contar os processos dos clientes:', error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchProcessCounts();
  }, []);

  // Detectar parâmetros da URL para filtro de aniversário
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filter = searchParams.get('filter');
    const clientId = searchParams.get('clientId');
    
    if (filter === 'birthday' && clientId) {
      // Filtrar para mostrar apenas o cliente específico
      setBirthdayClientId(clientId);
      
      // Marcar notificação como visualizada
      markNotificationAsViewed(clientId);
      
      // Mostrar toast informativo
      toast({
        title: "🎂 Filtro de Aniversário Ativo",
        description: "Mostrando cliente que está fazendo aniversário hoje",
        duration: 5000,
      });
      
      // Limpar URL após aplicar filtro (opcional)
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, toast]);

  // /clients?open=<id>: abre o cliente com os processos dele (ex.: vindo das Intimações)
  // Aviso do sininho: abre a lista de entradas para revisar
  useEffect(() => {
    if (new URLSearchParams(location.search).get('entradas')) {
      setIntakeOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search]);

  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (!openId || clients.length === 0) return;
    const client = clients.find(c => c.id === openId);
    if (client) handleView(client);
    window.history.replaceState({}, '', window.location.pathname);
  }, [location.search, clients]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setClients((data || []) as Client[]);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os clientes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const dataToSave = {
        ...formData,
        birth_date: formData.birth_date?.toISOString().split('T')[0] || null,
        lgpd_consent_date: formData.lgpd_consent_date?.toISOString() || null,
        user_id: user.id,
        updated_at: new Date().toISOString()
      };

      if (editingClient) {
        const { data: updated, error } = await supabase
          .from('clients')
          .update(dataToSave)
          .eq('id', editingClient.id)
          .select();

        if (error) {
          console.error('Erro ao atualizar cliente:', error);
          
          if (error.code === '23505') {
            // Verificar qual constraint foi violada
            if (error.message?.includes('cpf_cnpj')) {
              toast({
                title: "CPF já cadastrado",
                description: "Este CPF já está cadastrado para outro cliente. Verifique os dados.",
                variant: "destructive"
              });
            } else if (error.message?.includes('email')) {
              toast({
                title: "Email já cadastrado",
                description: "Este email já está sendo usado por outro cliente. Use um email diferente.",
                variant: "destructive"
              });
            } else {
              toast({
                title: "Dados duplicados",
                description: "Alguns dados já estão cadastrados. Verifique as informações.",
                variant: "destructive"
              });
            }
          } else if (error.code === '409' || error.status === 409) {
            toast({
              title: "Conflito no banco de dados",
              description: "Dados duplicados detectados. Verifique as informações.",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Erro ao atualizar",
              description: error.message || "Erro desconhecido ao atualizar cliente",
              variant: "destructive"
            });
          }
          return;
        }
        
        // Se nenhuma linha foi atualizada (possível RLS ou ID inválido), tratar como erro
        if (!updated || updated.length === 0) {
          toast({
            title: "Nenhum registro atualizado",
            description: "Cliente não encontrado ou sem permissão para atualizar.",
            variant: "destructive"
          });
          return;
        }
        
        toast({
          title: "Sucesso",
          description: "Cliente atualizado com sucesso"
        });

        // Atualiza estado local imediatamente para refletir no painel de detalhes
        const updatedClient = updated[0] as unknown as Client;
        setClients(prev => prev.map(c => c.id === updatedClient.id ? { ...c, ...updatedClient } : c));
        // Se o cliente está aberto no painel de detalhes, sincroniza também
        if (selectedClient && selectedClient.id === updatedClient.id) {
          setSelectedClient(updatedClient);
        }
      } else {
        const { error } = await supabase
          .from('clients')
          .insert([dataToSave]);

        if (error) {
          console.error('Erro ao cadastrar cliente:', error);
          
          if (error.code === '23505') {
            // Verificar qual constraint foi violada
            if (error.message?.includes('cpf_cnpj')) {
              toast({
                title: "CPF já cadastrado",
                description: "Este CPF já está cadastrado para outro cliente. Verifique os dados.",
                variant: "destructive"
              });
            } else if (error.message?.includes('email')) {
              toast({
                title: "Email já cadastrado",
                description: "Este email já está sendo usado por outro cliente. Use um email diferente.",
                variant: "destructive"
              });
            } else {
              toast({
                title: "Dados duplicados",
                description: "Alguns dados já estão cadastrados. Verifique as informações.",
                variant: "destructive"
              });
            }
          } else if (error.code === '409' || error.status === 409) {
            toast({
              title: "Conflito no banco de dados",
              description: "Dados duplicados detectados. Verifique as informações.",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Erro ao cadastrar",
              description: error.message || "Erro desconhecido ao cadastrar cliente",
              variant: "destructive"
            });
          }
          return;
        }
        
        toast({
          title: "Sucesso",
          description: "Cliente cadastrado com sucesso"
        });
      }

      setIsDialogOpen(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (error: any) {
      console.error('Erro ao salvar cliente:', error);
      
      let errorMessage = "Não foi possível salvar o cliente";
      
      if (error.code === '23505') {
        errorMessage = "CPF já cadastrado para outro cliente";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      client_type: "person",
      cpf_cnpj: "",
      rg_ie: "",
      birth_date: undefined,
      nationality: "Brasileira",
      marital_status: "",
      profession: "",
      email: "",
      phone: "",
      mobile: "",
      alternative_phone: "",
      address: "",
      address_number: "",
      complement: "",
      district: "",
      city: "",
      state: "",
      zip_code: "",
      country: "Brasil",
      how_found_us: "",
      referral_source: "",
      legal_areas: [],
      client_priority: "medium",
      notes: "",
      special_requirements: "",
      confidentiality_level: "normal",
      lgpd_consent: false,
      lgpd_consent_date: undefined,
      marketing_consent: false,
      data_sharing_consent: false,
    });
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      client_type: client.client_type,
      cpf_cnpj: client.cpf_cnpj || "",
      rg_ie: client.rg_ie || "",
      // Evita o famoso off-by-one ao interpretar "YYYY-MM-DD" como UTC
      // Coloca meio-dia local para preservar o dia ao formatar no frontend
      birth_date: client.birth_date ? new Date(`${client.birth_date}T12:00:00`) : undefined,
      nationality: client.nationality || "Brasileira",
      marital_status: client.marital_status || "",
      profession: client.profession || "",
      email: client.email,
      phone: client.phone || "",
      mobile: client.mobile || "",
      alternative_phone: client.alternative_phone || "",
      address: client.address || "",
      address_number: client.address_number || "",
      complement: client.complement || "",
      district: client.district || "",
      city: client.city || "",
      state: client.state || "",
      zip_code: client.zip_code || "",
      country: client.country || "Brasil",
      how_found_us: client.how_found_us || "",
      referral_source: client.referral_source || "",
      legal_areas: client.legal_areas || [],
      client_priority: client.client_priority || "medium",
      notes: client.notes || "",
      special_requirements: client.special_requirements || "",
      confidentiality_level: client.confidentiality_level || "normal",
      lgpd_consent: client.lgpd_consent,
      lgpd_consent_date: client.lgpd_consent_date ? new Date(client.lgpd_consent_date) : undefined,
      marketing_consent: client.marketing_consent || false,
      data_sharing_consent: client.data_sharing_consent || false,
    });
    setIsDialogOpen(true);
  };

  // Exclusão: mostra o que está ligado ao cliente antes de excluir
  const [deleteTarget, setDeleteTarget] = useState<{ client: Client; links: { label: string; count: number }[] } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const LINKED_TABLES = [
    { table: 'cases', label: 'processo(s) judicial(is)' },
    { table: 'administrative_processes', label: 'processo(s) administrativo(s)' },
    { table: 'appointments', label: 'compromisso(s) na agenda' },
    { table: 'financial_entries', label: 'lançamento(s) financeiro(s)' },
    { table: 'documents', label: 'documento(s)' },
  ] as const;

  const handleDelete = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;
    const counts = await Promise.all(
      LINKED_TABLES.map(({ table }) =>
        supabase.from(table as any).select('id', { count: 'exact', head: true }).eq('client_id', id).then(({ count }) => count || 0)
      )
    );
    setDeleteTarget({
      client,
      links: LINKED_TABLES.map((t, i) => ({ label: t.label, count: counts[i] })).filter(l => l.count > 0),
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.client.id;
    setDeleting(true);
    try {
      // Mantém processos, compromissos e lançamentos; apenas tira o vínculo com o cliente
      for (const { table } of LINKED_TABLES) {
        const { error } = await supabase.from(table as any).update({ client_id: null }).eq('client_id', id);
        if (error) throw error;
      }
      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) throw error;
      toast({ title: "Cliente excluído", description: `${deleteTarget.client.name} foi removido.` });
      setDeleteTarget(null);
      if (selectedClient?.id === id) setSelectedClient(null);
      fetchClients();
    } catch (error: any) {
      console.error('Erro ao excluir cliente:', error);
      toast({
        title: "Não foi possível excluir",
        description: error?.message || "Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleView = (client: Client) => {
    setSelectedClient(client);
    fetchClientProcesses(client.id);
  };

  // Função para truncar texto
  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Função para buscar processos do cliente
  const fetchClientProcesses = async (clientId: string) => {
    setLoadingProcesses(true);
    try {
      // Buscar processos onde o cliente é principal
      const { data: mainProcesses, error: mainError } = await supabase
        .from('cases')
        .select(`
          id,
          title,
          case_number,
          case_number_cnj,
          status,
          priority,
          case_type,
          court_level,
          filing_date,
          created_at,
          case_clients (
            client_id,
            is_primary,
            clients (
              id,
              name,
              email
            )
          )
        `)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      // Buscar processos onde o cliente participa como litisconsorte
      const { data: litisconsorcioProcesses, error: litisconsorcioError } = await supabase
        .from('case_clients')
        .select(`
          case_id,
          is_primary,
          cases (
            id,
            title,
            case_number,
            case_number_cnj,
            status,
            priority,
            case_type,
            court_level,
            filing_date,
            created_at
          )
        `)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (mainError) throw mainError;
      if (litisconsorcioError) throw litisconsorcioError;

      // Combinar processos principais e de litisconsórcio
      const allProcesses = [
        ...(mainProcesses || []).map(process => ({
          ...process,
          is_primary_client: true,
          client_role: 'Cliente Principal'
        })),
        ...(litisconsorcioProcesses || [])
          .filter(cc => cc.cases) // Filtrar casos válidos
          .map(cc => ({
            ...cc.cases,
            is_primary_client: cc.is_primary,
            client_role: cc.is_primary ? 'Cliente Principal' : 'Litisconsorte'
          }))
      ];

      // Remover duplicatas (caso o cliente seja principal E litisconsorte no mesmo processo)
      const uniqueProcesses = allProcesses.filter((process, index, self) =>
        index === self.findIndex(p => p.id === process.id)
      );

      // Processos administrativos do cliente
      const { data: adminProcesses } = await (supabase as any)
        .from('administrative_processes')
        .select('id, title, description, process_number, status, priority, procedure_type, administrative_agency, created_at')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      setClientProcesses([
        ...uniqueProcesses.map((p) => ({ ...p, process_type: 'case' })),
        ...(adminProcesses || []).map((p: any) => ({
          ...p,
          title: p.title || p.description,
          process_type: 'administrative',
          client_role: 'Cliente Principal',
        })),
      ]);
    } catch (error) {
      console.error('Erro ao buscar processos do cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os processos do cliente",
        variant: "destructive"
      });
    } finally {
      setLoadingProcesses(false);
    }
  };

  // Funções para abrir modais de estatísticas
  const openStatsModal = (type: string, title: string, filteredClients: Client[]) => {
    console.log('openStatsModal chamado:', type, title, filteredClients.length);
    setStatsModalType(type);
    setStatsModalTitle(title);
    setStatsModalClients(filteredClients);
    setStatsModalOpen(true);
  };

  const getVipClients = () => {
    return clients.filter(c => c.client_priority === 'vip');
  };

  const getCompanies = () => {
    return clients.filter(c => c.client_type === 'company');
  };

  const getNewThisMonth = () => {
    return clients.filter(c => {
      const clientDate = new Date(c.created_at);
      const now = new Date();
      return clientDate.getMonth() === now.getMonth() && clientDate.getFullYear() === now.getFullYear();
    });
  };

  const filteredClients = clients
    .filter(client => {
      // Filtro de aniversário (prioridade máxima)
      if (birthdayClientId) {
        return client.id === birthdayClientId;
      }
      if (kpiFilter && !kpiFilter.ids.has(client.id)) return false;
      
      const matchesSearch = 
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.cpf_cnpj && client.cpf_cnpj.includes(searchTerm));
      
      const matchesType = clientTypeFilter === 'all' || client.client_type === clientTypeFilter;
      const matchesPriority = priorityFilter === 'all' || client.client_priority === priorityFilter;
      const matchesLegalArea = legalAreaFilter === 'all' || 
        (client.legal_areas && client.legal_areas.includes(legalAreaFilter));
      
      return matchesSearch && matchesType && matchesPriority && matchesLegalArea;
    })
    .sort((a, b) => {
      // Na aba "Aniversários do mês", em ordem de data
      if (kpiFilter?.type === 'birthdays') {
        return (birthdayInfo(a.birth_date)?.day || 0) - (birthdayInfo(b.birth_date)?.day || 0);
      }
      // Aniversariantes do dia sempre primeiro
      const aIsBirthday = isBirthdayToday(a.birth_date);
      const bIsBirthday = isBirthdayToday(b.birth_date);
      
      if (aIsBirthday && !bIsBirthday) return -1;
      if (!aIsBirthday && bIsBirthday) return 1;
      
      // Caso contrário, ordenar por nome
      return a.name.localeCompare(b.name, 'pt-BR');
    });

  const getStats = () => {
    const total = clients.length;
    const vipClients = clients.filter(c => c.client_priority === 'vip').length;
    const companies = clients.filter(c => c.client_type === 'company').length;
    const thisMonth = clients.filter(c => {
      const clientDate = new Date(c.created_at);
      const now = new Date();
      return clientDate.getMonth() === now.getMonth() && clientDate.getFullYear() === now.getFullYear();
    }).length;

    return { total, vipClients, companies, thisMonth };
  };

  const stats = getStats();

  const legalPriorityClients = clients.filter((c) => getLegalPriority(c));
  const birthdaysThisMonth = clients.filter((c) => {
    if (!c.birth_date) return false;
    const birth = c.birth_date.includes('T') ? new Date(c.birth_date) : new Date(`${c.birth_date}T12:00:00`);
    return birth.getMonth() === new Date().getMonth();
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-4 md:p-6">
      {/* Cabeçalho */}
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Relacionamento</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Clientes</h1>
          <p className="mt-1 text-sm text-slate-500">
            {stats.total} cliente{stats.total === 1 ? '' : 's'} · {stats.vipClients} VIP
          </p>
          {birthdayClientId && (
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-0.5 text-xs font-medium text-pink-700">
                🎂 Filtro de aniversário ativo
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBirthdayClientId(null)}
                className="h-7 text-xs text-pink-600 hover:text-pink-800"
              >
                Limpar filtro
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          onClick={() => setIntakeOpen(true)}
          className="h-9 rounded-xl border-slate-200 bg-white text-[13px] text-slate-700 shadow-none hover:bg-slate-50"
        >
          <Link2 className="mr-2 h-4 w-4 text-[#9a7f52]" />
          Link de cadastro
        </Button>
        <ClientIntakePanel open={intakeOpen} onOpenChange={setIntakeOpen} onClientCreated={fetchClients} />
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingClient(null);
                resetForm();
              }}
              className="h-9 rounded-xl bg-slate-900 text-[13px] text-white shadow-sm hover:bg-slate-800"
            >
              <Plus className="mr-2 h-4 w-4 text-[#e8d5a8]" />
              Novo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl border border-[#c4ab83]/30" aria-describedby="client-form-description">
            <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
              {/* Monograma AS como marca d'água */}
              <img
                src="/imgs/logos allana/letra_A_logo.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
              />
              <div className="relative flex items-center gap-4 text-left">
                <img
                  src="/imgs/logos allana/letra_A_logo.png"
                  alt="Allana Sthéfany"
                  className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
                />
                <div>
                  <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                    {editingClient ? "Editar Cliente" : "Novo Cliente"}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-sm text-slate-500" id="client-form-description">
                    Preencha os campos abaixo para {editingClient ? "atualizar" : "cadastrar"} o cliente.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <ClientForm
              formData={formData}
              setFormData={(data) => setFormData(data as FormDataType)}
              onSubmit={handleSubmit}
              onCancel={() => setIsDialogOpen(false)}
              isEditing={!!editingClient}
            />
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {/* Indicadores */}
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-5">
        {[
          { label: 'Total', value: stats.total, hint: 'clientes cadastrados', list: clients, type: 'total', title: 'Todos os Clientes' },
          { label: 'VIP', value: stats.vipClients, hint: 'prioridade VIP', list: getVipClients(), type: 'vip', title: 'Clientes VIP', tone: 'text-amber-600' },
          { label: 'Prioridade legal', value: legalPriorityClients.length, hint: 'idosos e crianças', list: legalPriorityClients, type: 'priority', title: 'Prioridade de tramitação', tone: 'text-emerald-600' },
          { label: 'Aniversários', value: birthdaysThisMonth.length, hint: 'em ' + new Date().toLocaleDateString('pt-BR', { month: 'long' }), list: birthdaysThisMonth, type: 'birthdays', title: 'Aniversariantes do mês', tone: 'text-pink-600' },
          { label: 'Novos no mês', value: stats.thisMonth, hint: 'cadastrados este mês', list: getNewThisMonth(), type: 'new', title: 'Novos Este Mês' },
        ].map((kpi, index) => (
          <button
            key={kpi.label}
            type="button"
            onClick={() => setKpiFilter(current =>
              current?.type === kpi.type || kpi.type === 'total'
                ? null
                : { type: kpi.type, title: kpi.title, ids: new Set(kpi.list.map(c => c.id)) }
            )}
            className={`group relative px-4 py-4 text-left transition-colors duration-200 animate-in fade-in slide-in-from-bottom-1 fill-mode-both hover:bg-[#faf7f2] last:col-span-2 sm:last:col-span-1 ${(kpiFilter?.type === kpi.type || (!kpiFilter && kpi.type === 'total')) ? 'bg-[#faf7f2]' : 'bg-white'}`}
            style={{ animationDelay: `${index * 40}ms` }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{kpi.label}</div>
            <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${kpi.value && kpi.tone ? kpi.tone : 'text-slate-900'}`}>{kpi.value}</div>
            <div className="mt-0.5 text-[11px] text-slate-400">{kpi.hint}</div>
            {(kpiFilter?.type === kpi.type || (!kpiFilter && kpi.type === 'total')) && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#c4ab83]" />
            )}
          </button>
        ))}
      </section>

      {/* Busca e filtros */}
      <ClientFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        clientTypeFilter={clientTypeFilter}
        setClientTypeFilter={setClientTypeFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        legalAreaFilter={legalAreaFilter}
        setLegalAreaFilter={setLegalAreaFilter}
        totalClients={clients.length}
        filteredCount={filteredClients.length}
        typeCounts={{ all: clients.length, person: clients.length - stats.companies, company: stats.companies }}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {kpiFilter && (
        <div className="flex items-center gap-2 text-sm text-slate-600 animate-in fade-in duration-200">
          <span>Mostrando:</span>
          <button
            type="button"
            onClick={() => setKpiFilter(null)}
            className="flex items-center gap-1.5 rounded-full bg-[#c4ab83]/15 px-3 py-1 text-[13px] font-medium text-[#7a6440] hover:bg-[#c4ab83]/25"
          >
            {kpiFilter.title}
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Lista de clientes */}
      {filteredClients.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center animate-in fade-in duration-300">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#faf7f2]">
            <Users className="h-5 w-5 text-[#c4ab83]" />
          </div>
          <p className="font-medium text-slate-900">Nenhum cliente encontrado</p>
          <p className="mt-1 text-sm text-slate-500">
            {kpiFilter || searchTerm || clientTypeFilter !== 'all' || priorityFilter !== 'all' || legalAreaFilter !== 'all'
              ? 'Tente ajustar os filtros.'
              : 'Comece cadastrando seu primeiro cliente.'}
          </p>
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filteredClients.map((client, index) => (
            <div
              key={client.id}
              className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-300"
              style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
            >
              <ClientCard
                client={client}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                isBirthday={isBirthdayToday(client.birth_date)}
                processCount={processCounts[client.id] || 0}
                showBirthDate={kpiFilter?.type === 'birthdays'}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/70 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                <tr>
                  <th className="px-4 py-2.5">Cliente</th>
                  <th className="px-4 py-2.5">CPF/CNPJ</th>
                  <th className="px-4 py-2.5">Telefone</th>
                  <th className="px-4 py-2.5">Cidade</th>
                  <th className="px-4 py-2.5 text-center">Processos</th>
                  <th className="px-4 py-2.5 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredClients.map((client) => {
                  const phone = getClientPhone(client);
                  const priority = getLegalPriority(client);
                  return (
                    <tr key={client.id} className="group transition-colors hover:bg-[#faf7f2]/60">
                      <td className="px-4 py-2.5">
                        <button type="button" onClick={() => handleView(client)} className="flex items-center gap-3 text-left">
                          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${client.client_type === 'company' ? 'bg-slate-700' : 'bg-gradient-to-br from-[#c4ab83] to-[#9a7f52]'}`}>
                            {client.client_type === 'company' ? <Building className="h-4 w-4" /> : getInitials(client.name)}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-slate-900 group-hover:text-[#9a7f52]">
                              {client.name}
                              {isBirthdayToday(client.birth_date) && ' 🎂'}
                            </span>
                            <span className="flex gap-1.5 text-[11px]">
                              {kpiFilter?.type === 'birthdays' && birthdayInfo(client.birth_date) && (
                                <span className="font-semibold text-pink-600">
                                  🎂 {birthdayInfo(client.birth_date)!.label}
                                  {birthdayInfo(client.birth_date)!.when && ` · ${birthdayInfo(client.birth_date)!.when}`}
                                </span>
                              )}
                              {client.client_priority === 'vip' && <span className="font-semibold text-amber-600">★ VIP</span>}
                              {priority && <span className="text-emerald-700">{priority.label}</span>}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-slate-600">{client.cpf_cnpj || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        {phone ? (
                          <span className="flex items-center gap-2">
                            <a href={whatsappLink(phone)} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">
                              {phone}
                            </a>
                            {isBirthdayToday(client.birth_date) && (
                              <a
                                href={whatsappLink(phone, birthdayMessage(client.name))}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-full bg-pink-50 px-2 py-0.5 text-[11px] font-semibold text-pink-700 hover:bg-pink-100"
                                title="Abre o WhatsApp com a mensagem pronta"
                              >
                                🎂 Parabéns
                              </a>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                        {[client.city, client.state].filter(Boolean).join('/') || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-center tabular-nums text-slate-700">{processCounts[client.id] || 0}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right">
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-amber-700" onClick={() => handleEdit(client)}>
                          Editar
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 font-semibold text-[#7a6440] hover:bg-[#c4ab83]/10" onClick={() => handleView(client)}>
                          Abrir
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md rounded-2xl border-slate-200">
          <DialogHeader className="text-left">
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <DialogTitle className="text-lg font-semibold text-slate-900">Excluir {deleteTarget?.client.name}?</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              O cadastro do cliente será apagado. Não dá para desfazer.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && deleteTarget.links.length > 0 && (
            <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">Este cliente está ligado a:</p>
              <ul className="mt-1 list-disc pl-5">
                {deleteTarget.links.map(l => <li key={l.label}>{l.count} {l.label}</li>)}
              </ul>
              <p className="mt-2 text-[13px]">Eles <strong>não serão apagados</strong>: continuam no sistema, só ficam sem cliente vinculado.</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting} className="rounded-xl text-slate-600">Cancelar</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="rounded-xl bg-red-600 text-white hover:bg-red-700">
              {deleting ? 'Excluindo...' : 'Excluir cliente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={(open) => {
        if (!open) setSelectedClient(null);
      }}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl border border-[#c4ab83]/30">
          {selectedClient && (() => {
            const phone = getClientPhone(selectedClient);
            const legalPriority = getLegalPriority(selectedClient);
            const age = selectedClient.birth_date ? calculateAge(new Date(`${selectedClient.birth_date}T12:00:00`)) : null;
            const field = (label: string, value?: string | number | null, mono = false) => (
              <div className="min-w-0">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</dt>
                <dd className={`mt-0.5 break-words text-sm ${value ? 'text-slate-800' : 'italic text-slate-400'} ${mono && value ? 'font-mono' : ''}`}>
                  {value || 'Não informado'}
                </dd>
              </div>
            );
            const tabTrigger = 'rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm';
            const statusStyle = (status?: string) => {
              const s = (status || '').toLowerCase();
              if (s.includes('andamento')) return 'bg-emerald-50 text-emerald-700';
              if (s.includes('aguardando')) return 'bg-amber-50 text-amber-700';
              if (s.includes('suspen')) return 'bg-orange-50 text-orange-700';
              if (s.includes('conclu') || s.includes('arquiv')) return 'bg-slate-100 text-slate-600';
              return 'bg-blue-50 text-blue-700';
            };

            return (
              <>
                <DialogHeader className="relative -m-6 mb-5 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
                  <img
                    src="/imgs/logos allana/letra_A_logo.png"
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
                  />
                  <div className="relative flex items-center gap-4 text-left">
                    <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-lg font-semibold text-white ${selectedClient.client_type === 'company' ? 'bg-gradient-to-br from-slate-600 to-slate-800' : 'bg-gradient-to-br from-[#c4ab83] to-[#9a7f52]'}`}>
                      {selectedClient.client_type === 'company' ? <Building className="h-6 w-6" /> : getInitials(selectedClient.name)}
                    </span>
                    <div className="min-w-0">
                      <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">{selectedClient.name}</DialogTitle>
                      <p className="mt-0.5 text-sm text-slate-500">
                        {[
                          selectedClient.cpf_cnpj && `${selectedClient.client_type === 'company' ? 'CNPJ' : 'CPF'} ${selectedClient.cpf_cnpj}`,
                          [selectedClient.city, selectedClient.state].filter(Boolean).join('/'),
                          age !== null && `${age} anos`,
                        ].filter(Boolean).join(' · ')}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {selectedClient.client_priority === 'vip' && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">★ VIP</span>
                        )}
                        {legalPriority && (
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${legalPriority.strong ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                            {legalPriority.label}
                          </span>
                        )}
                        {phone && (
                          <a href={whatsappLink(phone)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100">
                            WhatsApp {phone}
                          </a>
                        )}
                        {selectedClient.email && (
                          <a href={`mailto:${selectedClient.email}`} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200">
                            {selectedClient.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogHeader>

                <Tabs defaultValue="personal" className="w-full">
                  <TabsList className="mb-5 grid h-auto w-full grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-4">
                    <TabsTrigger value="personal" className={tabTrigger}>Dados pessoais</TabsTrigger>
                    <TabsTrigger value="processes" className={tabTrigger}>
                      Processos{!loadingProcesses && ` (${clientProcesses.length})`}
                    </TabsTrigger>
                    <TabsTrigger value="address" className={tabTrigger}>Endereço</TabsTrigger>
                    <TabsTrigger value="lgpd" className={tabTrigger}>LGPD e notas</TabsTrigger>
                  </TabsList>

                  {/* Processos */}
                  <TabsContent value="processes" className="mt-0">
                    {loadingProcesses ? (
                      <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                        <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-[#c4ab83] border-t-transparent" />
                        Carregando processos...
                      </div>
                    ) : clientProcesses.length > 0 ? (
                      <div className="space-y-2">
                        {clientProcesses.map((process) => {
                          const isAdmin = process.process_type === 'administrative';
                          const number = isAdmin ? process.process_number : process.case_number_cnj || process.case_number;
                          return (
                            <button
                              key={`${process.process_type}-${process.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedClient(null);
                                navigate(isAdmin ? `/administrative-process/${process.id}` : `/processo/${process.id}`);
                              }}
                              className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors hover:border-[#c4ab83]/70 hover:bg-[#faf7f2]"
                            >
                              <span className={`h-9 w-1 shrink-0 rounded-full ${isAdmin ? 'bg-blue-500' : 'bg-[#c4ab83]'}`} />
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                                  <span className={`font-semibold uppercase tracking-[0.1em] ${isAdmin ? 'text-blue-600' : 'text-[#9a7f52]'}`}>
                                    {isAdmin ? 'Administrativo' : 'Judicial'}
                                  </span>
                                  {number && <span className="font-mono text-[13px] font-medium text-slate-600">{number}</span>}
                                  {process.client_role === 'Litisconsorte' && (
                                    <span className="rounded-full bg-purple-50 px-1.5 text-[10px] font-medium text-purple-700">Litisconsorte</span>
                                  )}
                                </div>
                                <p className="truncate text-sm font-medium text-slate-900 group-hover:text-[#9a7f52]">
                                  {process.title || 'Processo sem título'}
                                </p>
                              </div>
                              <span className={`hidden shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize sm:inline ${statusStyle(process.status)}`}>
                                {(process.status || 'Em andamento').replace(/-/g, ' ')}
                              </span>
                              <ExternalLink className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-[#9a7f52]" />
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 py-10 text-center">
                        <Scale className="mb-2 h-6 w-6 text-[#c4ab83]" />
                        <p className="font-medium text-slate-900">Nenhum processo vinculado</p>
                        <p className="mt-1 text-sm text-slate-500">Este cliente ainda não tem processos no sistema.</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Dados pessoais */}
                  <TabsContent value="personal" className="mt-0">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                      {field('Tipo', selectedClient.client_type === 'company' ? 'Pessoa jurídica' : 'Pessoa física')}
                      {field('CPF/CNPJ', selectedClient.cpf_cnpj, true)}
                      {field('RG/IE', selectedClient.rg_ie, true)}
                      {field('Telefone', selectedClient.phone)}
                      {field('Celular', selectedClient.mobile)}
                      {field('E-mail', selectedClient.email)}
                      {selectedClient.birth_date && field('Nascimento', `${new Date(`${selectedClient.birth_date}T12:00:00`).toLocaleDateString('pt-BR')}${age !== null ? ` (${age} anos)` : ''}`)}
                      {field('Nacionalidade', selectedClient.nationality)}
                      {selectedClient.client_type === 'person' && field('Estado civil', selectedClient.marital_status)}
                      {selectedClient.client_type === 'person' && field('Profissão', selectedClient.profession)}
                      {field('Prioridade', { vip: 'VIP', high: 'Alta', medium: 'Média', low: 'Baixa' }[selectedClient.client_priority || 'medium'])}
                      {field('Cadastrado em', new Date(selectedClient.created_at).toLocaleDateString('pt-BR'))}
                    </dl>
                    {selectedClient.legal_areas && selectedClient.legal_areas.length > 0 && (
                      <div className="mt-5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Áreas jurídicas</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {selectedClient.legal_areas.map((area) => (
                            <span key={area} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700">{area}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Endereço */}
                  <TabsContent value="address" className="mt-0">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                      {field('Endereço', [selectedClient.address, selectedClient.address_number, selectedClient.complement].filter(Boolean).join(', '))}
                      {field('Bairro', selectedClient.district)}
                      {field('Cidade', selectedClient.city)}
                      {field('Estado', selectedClient.state)}
                      {field('CEP', selectedClient.zip_code, true)}
                      {field('País', selectedClient.country)}
                    </dl>
                  </TabsContent>

                  {/* LGPD e notas */}
                  <TabsContent value="lgpd" className="mt-0 space-y-5">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
                      {field('Consentimento LGPD', selectedClient.lgpd_consent ? `Sim${selectedClient.lgpd_consent_date ? ` (${new Date(selectedClient.lgpd_consent_date).toLocaleDateString('pt-BR')})` : ''}` : 'Não')}
                      {field('Marketing', selectedClient.marketing_consent ? 'Autorizado' : 'Não autorizado')}
                      {field('Compartilhamento de dados', selectedClient.data_sharing_consent ? 'Autorizado' : 'Não autorizado')}
                    </dl>
                    {selectedClient.notes && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Observações</p>
                        <p className="mt-1 whitespace-pre-line rounded-xl bg-slate-50 p-3 text-sm text-slate-800">{selectedClient.notes}</p>
                      </div>
                    )}
                    {selectedClient.special_requirements && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Requisitos especiais</p>
                        <p className="mt-1 whitespace-pre-line rounded-xl bg-slate-50 p-3 text-sm text-slate-800">{selectedClient.special_requirements}</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
                  <Button variant="outline" className="rounded-xl" onClick={() => setSelectedClient(null)}>Fechar</Button>
                  <Button
                    className="rounded-xl bg-slate-900 text-white hover:bg-slate-800"
                    onClick={() => {
                      const client = selectedClient;
                      setSelectedClient(null);
                      handleEdit(client);
                    }}
                  >
                    Editar cliente
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Estatísticas de Clientes */}
      <Dialog open={statsModalOpen} onOpenChange={setStatsModalOpen}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto z-[9999] bg-white shadow-2xl border border-[#c4ab83]/30">
          <DialogHeader className="relative -m-6 mb-5 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative text-left">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">{statsModalTitle}</DialogTitle>
              <p className="mt-1 text-sm text-slate-500">
                {statsModalClients.length} cliente{statsModalClients.length === 1 ? '' : 's'}
              </p>
            </div>
          </DialogHeader>

          {statsModalClients.length > 0 ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(statsModalType === 'birthdays'
                ? [...statsModalClients].sort((a, b) => Number(a.birth_date?.slice(8, 10)) - Number(b.birth_date?.slice(8, 10)))
                : statsModalClients
              ).map((client) => {
                const priority = getLegalPriority(client);
                const birth = client.birth_date ? new Date(`${client.birth_date.slice(0, 10)}T12:00:00`) : null;
                const detail =
                  statsModalType === 'birthdays' && birth
                    ? `🎂 ${birth.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}${isBirthdayToday(client.birth_date) ? ' · hoje!' : ''}`
                    : statsModalType === 'priority' && priority
                      ? priority.label
                      : statsModalType === 'new'
                        ? `Cadastrado em ${new Date(client.created_at).toLocaleDateString('pt-BR')}`
                        : [client.cpf_cnpj, [client.city, client.state].filter(Boolean).join('/')].filter(Boolean).join(' · ');
                return (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => {
                      setStatsModalOpen(false);
                      handleView(client);
                    }}
                    className="group flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors hover:border-[#c4ab83]/70 hover:bg-[#faf7f2]"
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${client.client_type === 'company' ? 'bg-slate-700' : 'bg-gradient-to-br from-[#c4ab83] to-[#9a7f52]'}`}>
                      {client.client_type === 'company' ? <Building className="h-4 w-4" /> : getInitials(client.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-slate-900 group-hover:text-[#9a7f52]">{client.name}</span>
                        {client.client_priority === 'vip' && <span className="shrink-0 text-[11px] font-semibold text-amber-600">★ VIP</span>}
                      </span>
                      <span className="block truncate text-xs text-slate-500">{detail || 'Sem informações adicionais'}</span>
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-[#9a7f52] opacity-0 transition-opacity group-hover:opacity-100">Abrir →</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 py-10 text-center">
              <Users className="mb-2 h-6 w-6 text-[#c4ab83]" />
              <p className="text-sm text-slate-500">Nenhum cliente nesta categoria.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const getAgePriorityStatus = (age: number, client_type: 'person' | 'company'): string => {
  if (client_type === 'company') {
    return 'Não se aplica';
  }
  if (age >= 60) {
    return 'Prioritário (60+)';
  }
  return 'Normal';
};

const calculateAge = (birthDate: Date) => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * Verifica se o cliente está fazendo aniversário hoje
 */
const isBirthdayToday = (birthDate: string | undefined): boolean => {
  if (!birthDate) return false;
  
  const today = new Date();
  const birth = birthDate.includes('T') ? new Date(birthDate) : new Date(`${birthDate}T12:00:00`);
  
  return birth.getMonth() === today.getMonth() && 
         birth.getDate() === today.getDate();
};

export default Clients;